import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './apiClient';

const contractKeys = {
  all: ['contracts'],
  lists: () => [...contractKeys.all, 'list'],
  list: (filters) => [...contractKeys.lists(), filters],
  archived: (filters) => [...contractKeys.all, 'archived', filters],
  counts: () => [...contractKeys.all, 'count'],
  count: (archived) => [...contractKeys.counts(), archived],
  details: () => [...contractKeys.all, 'detail'],
  detail: (id) => [...contractKeys.details(), id],
  subscribers: () => [...contractKeys.all, 'subscribers'],
  contractSubscribers: (contractId) => [...contractKeys.subscribers(), contractId],
  withSubscribers: () => [...contractKeys.all, 'with-subscribers'],
  additionals: () => [...contractKeys.all, 'additionals'],
  contractAdditionals: (contractId) => [...contractKeys.additionals(), contractId],
  insurance: () => [...contractKeys.all, 'insurance'],
  fireInsurance: () => [...contractKeys.insurance(), 'fire'],
  lifeInsurance: () => [...contractKeys.insurance(), 'life'],
  realStateInsurance: () => [...contractKeys.insurance(), 'real_state'],
  regularized: (filters) => [...contractKeys.all, 'regularized', filters],
};

export const useContracts = (filters = {}, archived = false) => {
  return useQuery({
    queryKey: archived ? contractKeys.archived(filters) : contractKeys.list(filters),
    queryFn: async () => {
      const cleanFilters = { ...filters };

      ['uuid_plan', 'uuid_group', 'has_subscribers'].forEach((key) => {
        if (cleanFilters[key] === '') {
          delete cleanFilters[key];
        }
      });

      const archivedString = String(archived);
      const response = await apiClient.get(`contracts/filter/${archivedString}`, {
        params: cleanFilters,
      });
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useContractsCount = (archived = false) => {
  return useQuery({
    queryKey: contractKeys.count(archived),
    queryFn: async () => {
      const archivedString = String(archived);
      const response = await apiClient.get(`contracts/count/${archivedString}`);
      return response.data;
    },
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
  });
};

export const useRegularizedContracts = (filters = {}) => {
  return useQuery({
    queryKey: contractKeys.regularized(filters),
    queryFn: async () => {
      const cleanFilters = { ...filters };

      if (cleanFilters.group === '') {
        delete cleanFilters.group;
      }

      const response = await apiClient.get('contracts/regularized', {
        params: cleanFilters,
      });
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useContractsWithSubscribers = () => {
  return useQuery({
    queryKey: contractKeys.withSubscribers(),
    queryFn: async () => {
      const response = await apiClient.get('contracts/with-subscribers');
      return response.data;
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};

export const useContract = (id, options = {}) => {
  return useQuery({
    queryKey: contractKeys.detail(id),
    queryFn: async () => {
      const response = await apiClient.get(`contracts/${id}`);
      return response.data;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

export const useCreateContract = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (contractData) => {
      const response = await apiClient.post('contracts', contractData);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contractKeys.lists() });
      queryClient.invalidateQueries({ queryKey: contractKeys.counts() });
    },
    onError: (error) => {
      console.error('Error creating contract:', error);
    },
  });
};

export const useUpdateContract = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (contractData) => {
      const response = await apiClient.put(`contracts/${contractData.uuid}`, contractData);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(contractKeys.detail(data.uuid), data);
      queryClient.invalidateQueries({ queryKey: contractKeys.lists() });
      queryClient.invalidateQueries({ queryKey: contractKeys.archived() });
    },
    onError: (error) => {
      console.error('Error updating contract:', error);
    },
  });
};

export const useDeleteContract = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const response = await apiClient.delete(`contracts/${id}`);
      return response.data;
    },
    onSuccess: (data, deletedId) => {
      queryClient.invalidateQueries({ queryKey: contractKeys.lists() });
      queryClient.invalidateQueries({ queryKey: contractKeys.counts() });
      queryClient.removeQueries({ queryKey: contractKeys.detail(deletedId) });
    },
    onError: (error) => {
      console.error('Error deleting contract:', error);
    },
  });
};

export const useReleaseContract = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (contractData) => {
      const { release_reason_obs, uuid_release_reason } = contractData;
      const requestData = { release_reason_obs, uuid_release_reason };

      const response = await apiClient.post(`contracts/release/${contractData.uuid}`, {
        data: requestData,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contractKeys.lists() });
      queryClient.invalidateQueries({ queryKey: contractKeys.counts() });
    },
    onError: (error) => {
      console.error('Error releasing contract:', error);
    },
  });
};

export const useCreateContractSubscriber = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (subscriberData) => {
      const response = await apiClient.post('contract_subscribers', subscriberData);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: contractKeys.detail(data.uuid_contract) });
      queryClient.invalidateQueries({
        queryKey: contractKeys.contractSubscribers(data.uuid_contract),
      });

      queryClient.invalidateQueries({ queryKey: contractKeys.counts() });
      queryClient.invalidateQueries({ queryKey: contractKeys.lists() });
      queryClient.invalidateQueries({ queryKey: contractKeys.all });
    },
    onError: (error) => {
      console.error('Error creating contract subscriber:', error);
    },
  });
};

export const useUpdateContractSubscriber = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (subscriberData) => {
      const { uuid_subscriber, uuid_contract } = subscriberData;
      const response = await apiClient.put(
        `contract_subscribers/${uuid_contract}/${uuid_subscriber}`,
        subscriberData
      );
      return response.data;
    },
    onSuccess: (data) => {
      // Invalidar el detalle del contrato
      queryClient.invalidateQueries({ queryKey: contractKeys.detail(data.uuid_contract) });
      // Invalidar los suscriptores del contrato
      queryClient.invalidateQueries({
        queryKey: contractKeys.contractSubscribers(data.uuid_contract),
      });
      // Invalidar el count de contratos
      queryClient.invalidateQueries({ queryKey: contractKeys.counts() });
      // Invalidar todas las listas de contratos
      queryClient.invalidateQueries({ queryKey: contractKeys.lists() });
      queryClient.invalidateQueries({ queryKey: contractKeys.all });
    },
    onError: (error) => {
      console.error('Error updating contract subscriber:', error);
    },
  });
};

export const useDeleteContractSubscriber = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ uuid_subscriber, uuid_contract }) => {
      const response = await apiClient.delete(
        `contract_subscribers/${uuid_contract}/${uuid_subscriber}`
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: contractKeys.detail(variables.uuid_contract) });
      queryClient.invalidateQueries({
        queryKey: contractKeys.contractSubscribers(variables.uuid_contract),
      });

      queryClient.invalidateQueries({ queryKey: contractKeys.counts() });
      queryClient.invalidateQueries({ queryKey: contractKeys.lists() });
      queryClient.invalidateQueries({ queryKey: contractKeys.all });
    },
    onError: (error) => {
      console.error('Error deleting contract subscriber:', error);
    },
  });
};

export const useCreateContractSubscriberArchived = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (subscriberData) => {
      const response = await apiClient.post('contract_subscribers/archived', subscriberData);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: contractKeys.detail(data.uuid_contract) });
      queryClient.invalidateQueries({ queryKey: contractKeys.count(true) });
      queryClient.invalidateQueries({ queryKey: contractKeys.archived() });
    },
    onError: (error) => {
      console.error('Error creating archived contract subscriber:', error);
    },
  });
};

export const useUpdateContractSubscriberArchived = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (subscriberData) => {
      const { uuid_subscriber, uuid_contract } = subscriberData;
      const response = await apiClient.put(
        `contract_subscribers/archived/${uuid_contract}/${uuid_subscriber}`,
        subscriberData
      );
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: contractKeys.detail(data.uuid_contract) });
      queryClient.invalidateQueries({ queryKey: contractKeys.count(true) });
      queryClient.invalidateQueries({ queryKey: contractKeys.archived() });
    },
    onError: (error) => {
      console.error('Error updating archived contract subscriber:', error);
    },
  });
};

export const useDeleteContractSubscriberArchived = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ uuid_subscriber, uuid_contract }) => {
      const response = await apiClient.delete(
        `contract_subscribers/archived/${uuid_contract}/${uuid_subscriber}`
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: contractKeys.detail(variables.uuid_contract) });
      queryClient.invalidateQueries({ queryKey: contractKeys.count(true) });
      queryClient.invalidateQueries({ queryKey: contractKeys.archived() });
    },
    onError: (error) => {
      console.error('Error deleting archived contract subscriber:', error);
    },
  });
};

export const useCreateContractAdditional = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (additionalData) => {
      const response = await apiClient.post('contract_additionals', additionalData);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: contractKeys.contractAdditionals(data.uuid_contract),
      });
      queryClient.invalidateQueries({ queryKey: contractKeys.detail(data.uuid_contract) });
      
      queryClient.invalidateQueries({ queryKey: contractKeys.lists() });
      queryClient.invalidateQueries({ queryKey: contractKeys.all });
    },
    onError: (error) => {
      console.error('Error creating contract additional:', error);
    },
  });
};

export const useUpdateContractAdditional = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (additionalData) => {
      const response = await apiClient.put(
        `contract_additionals/${additionalData.uuid}`,
        additionalData
      );
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: contractKeys.contractAdditionals(data.uuid_contract),
      });
      queryClient.invalidateQueries({ queryKey: contractKeys.detail(data.uuid_contract) });
      
      queryClient.invalidateQueries({ queryKey: contractKeys.lists() });
      queryClient.invalidateQueries({ queryKey: contractKeys.all });
    },
    onError: (error) => {
      console.error('Error updating contract additional:', error);
    },
  });
};

export const useDeleteContractAdditional = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (additionalData) => {
      const response = await apiClient.delete(
        `contract_additionals/${additionalData.uuid}`
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: contractKeys.contractAdditionals(variables.uuid_contract),
      });
      queryClient.invalidateQueries({ queryKey: contractKeys.detail(variables.uuid_contract) });
      
      queryClient.invalidateQueries({ queryKey: contractKeys.lists() });
      queryClient.invalidateQueries({ queryKey: contractKeys.all });
    },
    onError: (error) => {
      console.error('Error deleting contract additional:', error);
    },
  });
};

export const useGetFireInsuranceData = () => {
  return useMutation({
    mutationFn: async (params) => {
      const response = await apiClient.post('contracts/fire_insurance', params, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    },
    onError: (error) => {
      console.error('Error getting fire insurance data:', error);
    },
  });
};

export const useImportFireInsuranceData = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (file) => {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post('contracts/fire_insurance/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contractKeys.fireInsurance() });
      queryClient.invalidateQueries({ queryKey: contractKeys.lists() });
    },
    onError: (error) => {
      console.error('Error importing fire insurance data:', error);
    },
  });
};

export const useGetLifeInsuranceData = () => {
  return useMutation({
    mutationFn: async (params) => {
      const response = await apiClient.post('contracts/life_insurance', params, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    },
    onError: (error) => {
      console.error('Error getting life insurance data:', error);
    },
  });
};

export const useImportLifeInsuranceData = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (file) => {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post('contracts/life_insurance/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contractKeys.lifeInsurance() });
      queryClient.invalidateQueries({ queryKey: contractKeys.lists() });
    },
    onError: (error) => {
      console.error('Error importing life insurance data:', error);
    },
  });
};

export const useGetRealStateInsuranceData = () => {
  return useMutation({
    mutationFn: async (params) => {
      const response = await apiClient.post('contracts/real_state_taxes', params, {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      return response.data;
    },
    onError: (error) => {
      console.error('Error getting real state insurance data:', error);
    },
  });
};

export const useImportRealStateInsuranceData = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (file) => {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiClient.post('contracts/real_state_taxes/import', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: contractKeys.realStateInsurance() });
      queryClient.invalidateQueries({ queryKey: contractKeys.lists() });
    },
    onError: (error) => {
      console.error('Error importing real state insurance data:', error);
    },
  });
};
